import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Chart from "react-google-charts";
import { toast } from "react-toastify";
import { API_ENDPOINT, HEADER_TOKEN } from "../../constants/constants";
import { getDateFormat, getHeader } from "../../utils/util";

/* Consumption trend for a single trainer.
   Built entirely from the user document the Membership list already returns:
   `plan.slots` is 12 monthly windows of { start_date, count, used, end_date },
   so monthly candidate usage needs no extra endpoint. */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Index of the slot whose window contains `now` — mirrors the API's findSlotIndex. */
const currentSlotIndex = (slots) => {
    const now = new Date();
    return slots.findIndex(s => {
        const start = new Date(s.start_date), end = new Date(s.end_date);
        return !isNaN(start) && !isNaN(end) && now >= start && now <= end;
    });
};

const StatCard = ({ label, value, sub }) => (
    <div style={{ background: "var(--color-surface)", padding: "18px 18px 16px", borderTop: "3px solid var(--color-divider)" }}>
        <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>{label}</div>
        <div style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 38, lineHeight: 1.05, marginTop: 6 }}>{value}</div>
        {sub ? <div style={{ fontSize: 12, marginTop: 6 }} className="text-muted">{sub}</div> : null}
    </div>
);

const MembershipTrend = ({ user, onBack, onChanged }) => {
    const [ac, setAc] = useState("");
    const [saving, setSaving] = useState(false);

    const thisYear = new Date().getFullYear();
    const [year, setYear] = useState(thisYear);
    const [activity, setActivity] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const pastYears = Array.from({ length: 5 }, (_, i) => thisYear - i);

    const plan = user && user.plan ? user.plan : null;
    const slots = plan && Array.isArray(plan.slots) ? plan.slots : [];

    /* Calendar-month activity: assessments conducted, and every candidate who
       joined across all of them. Kept separate from plan.slots below, which are
       30-day billing windows rather than calendar months. */
    useEffect(() => {
        if (!user || !user.phone) return;
        setLoadingActivity(true);
        axios.post(API_ENDPOINT + "admin/trainer-assessments-overview",
            { phone: user.phone, year }, getHeader())
            .then(res => {
                setLoadingActivity(false);
                if (res.data.success) setActivity(res.data.message || []);
                else setActivity([]);
            })
            .catch(() => { setLoadingActivity(false); setActivity([]); });
    }, [user && user.phone, year]);

    const { activityData, yearAssessments, yearCandidates } = useMemo(() => {
        const rows = [["Month", "Candidates joined", "Assessments conducted"]];
        let a = 0, c = 0;
        MONTH_ABBR.forEach((m, i) => {
            const found = activity.find(r => r._id === i + 1);
            const ac_ = found ? (Number(found.assessmentCount) || 0) : 0;
            const cc_ = found ? (Number(found.candidateCount) || 0) : 0;
            a += ac_; c += cc_;
            rows.push([m, cc_, ac_]);
        });
        return { activityData: rows, yearAssessments: a, yearCandidates: c };
    }, [activity]);

    /* Plan-side totals, derived from the 30-day billing slots. */
    const { totalUsed, totalAllowance, thisMonth, curIdx } = useMemo(() => {
        const idx = currentSlotIndex(slots);
        let used = 0, allowance = 0;
        slots.forEach((s) => {
            used += Number(s.used) || 0;
            allowance += Number(s.count) || 0;
        });
        return {
            totalUsed: used,
            totalAllowance: allowance,
            thisMonth: idx >= 0 ? slots[idx] : null,
            curIdx: idx,
        };
    }, [slots]);

    const topupBalance = plan && plan.topup != null ? plan.topup : 0;
    const monthRemaining = thisMonth
        ? Math.max(0, (Number(thisMonth.count) || 0) - (Number(thisMonth.used) || 0))
        : null;

    const addTopUp = () => {
        if (ac === "" || Number(ac) <= 0) {
            toast("Please enter a candidate count", { position: "bottom-center", autoClose: 1000 });
            return;
        }
        setSaving(true);
        // Wire field stays `assessmentCount` — the API keeps it as a synced
        // legacy alias for the candidate count ($inc on plan.topup).
        axios.post(API_ENDPOINT + "admin/trainer-topup", { phone: user.phone, assessmentCount: ac }, HEADER_TOKEN)
            .then(res => {
                setSaving(false);
                const d = res.data;
                if (d.success) {
                    setAc("");
                    toast(d.message || "Top-up credited", { autoClose: 1200, position: "top-right" });
                    if (onChanged) onChanged();
                } else {
                    toast(d.message || "Could not credit top-up", { autoClose: 1200, position: "top-right" });
                }
            })
            .catch(() => { setSaving(false); toast("Request failed", { autoClose: 1200, position: "top-right" }) });
    };

    const chartOptions = {
        legend: "none",
        backgroundColor: "transparent",
        fontName: "Archivo",
        chartArea: { left: 48, top: 12, width: "84%", height: "72%" },
        hAxis: { textStyle: { fontSize: 10, color: "#7d7979" }, baselineColor: "#d7d3d3", gridlines: { color: "transparent" } },
        vAxes: {
            0: { textStyle: { fontSize: 10, color: "#9b9797" }, gridlines: { color: "#d7d3d3", count: 5 }, minValue: 0 },
            1: { textStyle: { fontSize: 10, color: "#9b9797" }, gridlines: { color: "transparent" }, minValue: 0 },
        },
        series: {
            0: { targetAxisIndex: 0, type: "bars", color: "#444141" },
            1: { targetAxisIndex: 1, type: "line", color: "#ec3013", lineWidth: 2.5, pointSize: 5 },
        },
        bar: { groupWidth: "55%" },
        height: 300,
    };

    return (
        <div>
            <a href="#back" onClick={(e) => { e.preventDefault(); onBack(); }}
                style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 14 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                Back to Membership
            </a>

            {/* IDENTITY */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                <div style={{ width: 48, height: 48, flex: "none", background: "var(--color-accent)", color: "var(--color-bg)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 18 }}>
                    {(user.name || "?").trim().charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>Consumption trend</div>
                    <h3 style={{ margin: "2px 0 0" }}>{user.name}</h3>
                    <div style={{ fontSize: 13, marginTop: 2 }} className="text-muted">
                        {user.email} · {user.phone}
                    </div>
                </div>
                <div style={{ textAlign: "right" }}>
                    {plan
                        ? <span className={plan.status === "Expired" ? "tag tag-neutral" : "tag tag-accent"}>{plan.name}</span>
                        : <span className="tag tag-neutral">Not subscribed</span>}
                    {plan && plan.endsOn && (
                        <div style={{ fontSize: 11, marginTop: 6 }} className="text-muted">
                            Ends {getDateFormat(new Date(plan.endsOn))}
                        </div>
                    )}
                </div>
            </div>

            {/* SUMMARY */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
                <StatCard label="Used this month" value={thisMonth ? (Number(thisMonth.used) || 0) : "—"}
                    sub={thisMonth ? `of ${Number(thisMonth.count) || 0} allowance` : "No active period"} />
                <StatCard label="Remaining this month" value={monthRemaining !== null ? monthRemaining : "—"}
                    sub={curIdx >= 0 ? `Period ${curIdx + 1} of ${slots.length}` : "No active period"} />
                <StatCard label="Total used" value={totalUsed} sub={`of ${totalAllowance} across ${slots.length} periods`} />
                <StatCard label="Top-up balance" value={topupBalance} sub="Extra candidates credited" />
            </div>

            {/* TREND */}
            <div style={{ background: "var(--color-surface)", padding: 18, marginTop: 16 }}>
                <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>Activity</div>
                        <h4 style={{ margin: "2px 0 0" }}>Monthly consumption · {year}</h4>
                        <div style={{ fontSize: 12, marginTop: 2 }} className="text-muted">
                            {yearAssessments} assessments · {yearCandidates} candidates joined in {year}
                        </div>
                    </div>
                    <select className="input" style={{ width: "auto", minHeight: 32, fontSize: 13 }}
                        value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {pastYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div style={{ display: "flex", gap: 18, marginBottom: 6, fontSize: 12 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 11, height: 11, background: "var(--color-neutral-800)", display: "inline-block" }}></span>Candidates joined
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 14, height: 3, background: "var(--color-accent)", display: "inline-block" }}></span>Assessments conducted
                    </span>
                </div>
                {loadingActivity
                    ? <div className="text-muted" style={{ fontSize: 13, padding: "32px 0", textAlign: "center" }}>Loading activity…</div>
                    : yearAssessments === 0 && yearCandidates === 0
                        ? <div className="text-muted" style={{ fontSize: 13, padding: "32px 0", textAlign: "center" }}>
                            No assessments conducted in {year}.
                        </div>
                        : <Chart chartType="ComboChart" width="100%" height="300px" data={activityData} options={chartOptions} />}
            </div>

            {/* TOP-UP + PERIOD BREAKDOWN */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16, marginTop: 16, alignItems: "start" }}>
                <div style={{ background: "var(--color-surface)", padding: 18 }}>
                    <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-accent)" }}>Adjust</div>
                    <h4 style={{ margin: "2px 0 4px" }}>Give additional top-up</h4>
                    <p style={{ fontSize: 13, margin: "0 0 14px" }} className="text-muted">
                        Credits extra candidates on top of the monthly allowance. The trainer is notified by email.
                    </p>
                    <div className="field" style={{ marginBottom: 12 }}>
                        <label>Additional candidates</label>
                        <input
                            className="input"
                            value={ac}
                            onChange={(e) => { if (/^\d*$/.test(e.target.value)) setAc(e.target.value) }}
                            placeholder="e.g. 100"
                        />
                    </div>
                    <button className="btn btn-primary" onClick={addTopUp} disabled={saving} style={{ width: "100%", justifyContent: "space-between", padding: "10px 14px" }}>
                        {saving ? "Crediting…" : "Credit top-up"}
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                    </button>
                    <div style={{ fontSize: 11, marginTop: 10 }} className="text-muted">
                        Current top-up balance: <strong>{topupBalance}</strong>
                    </div>
                </div>

                <div style={{ background: "var(--color-surface)", padding: "6px 18px 12px" }}>
                    <table className="table">
                        <thead>
                            <tr><th>Period</th><th>From</th><th>To</th><th>Used</th><th>Allowance</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                            {slots.map((s, i) => {
                                const used = Number(s.used) || 0;
                                const count = Number(s.count) || 0;
                                const isCurrent = i === curIdx;
                                return (
                                    <tr key={i} style={isCurrent ? { background: "var(--color-accent-100)" } : undefined}>
                                        <td className="text-muted">{i + 1}</td>
                                        <td>{isNaN(new Date(s.start_date)) ? "—" : getDateFormat(new Date(s.start_date))}</td>
                                        <td>{isNaN(new Date(s.end_date)) ? "—" : getDateFormat(new Date(s.end_date))}</td>
                                        <td style={{ fontFamily: "var(--font-heading)", fontWeight: 800 }}>{used}</td>
                                        <td>{count}</td>
                                        <td>
                                            {isCurrent
                                                ? <span className="tag tag-accent">Current</span>
                                                : used >= count && count > 0
                                                    ? <span className="tag tag-neutral">Exhausted</span>
                                                    : <span className="tag tag-neutral">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                            {slots.length === 0 && (
                                <tr><td colSpan={6} className="text-muted" style={{ textAlign: "center", padding: 24 }}>No billing periods</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MembershipTrend;
